import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import store, { actions, useDeviceCtx } from '../../store';
import SearchSelect from '../../components/SearchSelect';
import { deviceLabel } from '../../api/device';
import { useDictText } from '../../components/DictSelect';

// 设备工况 3 个子页 (设备树/工况 · 功率校准 · YAML预览) 共享顶部设备选择器.
// 切换 device 通过 Redux 同步, 不依赖 URL, 也不会被 subtab 链接清掉.
//
// 例外: ?deviceKey=X 作为**外部深链入口** (如工艺类型的"具备设备"弹窗点某台跳进来).
// 读到即写入 store 并把参数从 URL 抹掉 —— 只当一次性入场提示, 之后仍以 store 为准,
// 否则用户在页面里切了设备、URL 还挂着旧 key, 刷新会跳回别人的设备.

interface Props {
    label?: string;
    /** 是否在右侧补上能力提示 (标准功率 / 最小稳定) */
    showCaps?: boolean;
}

function DeviceTopSelector({ label = '当前设备', showCaps = true }: Props) {
    // 设备展示名的光源段走字典 label (light_source), 不裸显 code
    const lightText = useDictText('light_source');
    const dispatch = useDispatch();
    const ctx = useDeviceCtx();
    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
        if (!ctx.loaded) {
            (store.dispatch as any)(actions.device.load());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 深链入场: ?deviceKey=X → 选中该设备, 随后抹掉参数 (见顶部说明)
    useEffect(() => {
        const q = new URLSearchParams(location.search);
        const key = q.get('deviceKey');
        if (!key) return;
        dispatch(actions.device.setCur(key));
        q.delete('deviceKey');
        const qs = q.toString();
        history.replace(`${location.pathname}${qs ? `?${qs}` : ''}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const cur = ctx.devices.find((d) => d.deviceKey === ctx.curDeviceKey);

    return (
        <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-semibold">{label}</span>
            <SearchSelect
                className="w-80"
                value={ctx.curDeviceKey}
                onChange={(v) => dispatch(actions.device.setCur(v))}
                disabled={!ctx.loaded || ctx.devices.length === 0}
                placeholder={!ctx.loaded ? '加载中…' : '搜 deviceKey / 设备 / 架构'}
                options={ctx.devices.map((d) => ({
                    // 显示名见 deviceLabel; deviceKey 是业务主键, 一并展示 (同名变体仅靠功率难区分)
                    value: d.deviceKey,
                    label: `${d.archCode} · ${deviceLabel(d, lightText)} · ${d.deviceKey}`,
                }))}
            />
            {cur && (
                <span className="tag bg-slate-100 text-slate-600 font-mono" title="deviceKey (业务主键)">
                    {cur.deviceKey}
                </span>
            )}
            {showCaps && cur && (
                <>
                    {cur.capability?.stdW != null && (
                        <span className="tag bg-slate-100 text-slate-600">
                            标准 {cur.capability.stdW}W
                        </span>
                    )}
                    {cur.capability?.minStablePct != null && cur.capability.minStablePct > 0 && (
                        <span className="tag bg-amber-50 text-amber-700">
                            最小稳定 {cur.capability.minStablePct}%
                        </span>
                    )}
                    {cur.archCode && (
                        <span className="tag bg-brand-50 text-brand-700">{cur.archCode}</span>
                    )}
                </>
            )}
        </div>
    );
}

export default DeviceTopSelector;
